import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  angleForTime,
  dragTo,
  nextGear,
  TURN_MS,
  geared,
  pxPerRadian,
  marksFor,
  nearestDetent,
  release,
  snap,
  step,
  timeForAngle,
  type Detent,
  type DialState,
} from "../lib/dial.ts";
import { useMediaQuery } from "../lib/useMedia.ts";

/**
 * How much of the rim is engraved at once. Matched to the disk's geometry in CSS (300vw wide, so
 * a radius of 1.5 screens): at 45 degrees the arc overhangs both edges, which is what stops marks
 * running out at the sides, and the rim only drops about a tenth of the screen width, which is
 * what keeps the hour labels inside the dial's own band. About three hours of a 24-hour turn.
 */
const ARC = Math.PI / 4;
/** How far two fingers must spread, as a ratio, before the dial changes gear. */
const PINCH_STEP = 1.4;
/**
 * Labels are only drawn over the flat middle of the arc. Towards the edges the rim falls away
 * faster than the dial is tall, so a label there would hang below the disk and be cut in half;
 * the tick still appears, so the rim keeps its rhythm.
 */
const LABEL_ARC = ARC * 0.62;
/** The velocity estimate is smoothed, so one jittery sample cannot fling the dial. */
const SMOOTHING = 0.6;
/** Pointer events land about every 8-16 ms on a phone; anything tighter is a coalesced burst. */
const MIN_SAMPLE_MS = 8;
const HAPTIC_MS = 8;
/** Stable, so the default never rebuilds the bounds and restarts the frame loop. */
const EMPTY_BOUNDARIES: number[] = [];

const vibrate = (ms: number) => {
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    try {
      navigator.vibrate(ms);
    } catch {
      // A phone that refuses to buzz is not a reason to stop scrubbing.
    }
  }
};

/**
 * The time dial: a disk below the map whose rim is engraved with the time of day.
 *
 * One turn is one day, so the angle *is* the clock — a quarter turn is six hours wherever you are
 * in the trip. It is magnetic: pulled to the moments something was recorded, and it ticks as it
 * passes them. The rim tells you the time of day; the readout above tells you which day, and on a
 * trip longer than a day that readout is the only thing distinguishing one turn from the next.
 */
export function TimeDial({
  from,
  to,
  value,
  detents,
  settleOn,
  magnetic = true,
  boundaries = EMPTY_BOUNDARIES,
  timeZone,
  readout,
  thumb = null,
  turnMs = TURN_MS,
  onScrub,
  onHold,
  onZoom,
}: {
  /** The trip's first and last instant. */
  from: number;
  to: number;
  /** Where the playhead is now. */
  value: number;
  detents: Detent[];
  /**
   * The moments letting go may settle on, when that is a narrower set than the marks on the rim
   * — the day being turned through. Empty means nothing to settle on, so the dial stays put.
   */
  settleOn?: Detent[];
  /** Whether the moments pull the dial towards them. Off: it rests where it was let go. */
  magnetic?: boolean;
  /** Where each local day becomes the next; the dial resists crossing one. */
  boundaries?: number[];
  timeZone: string;
  /** What the dial is sitting on, in words. */
  readout: string;
  /** The recording under the needle, if one has a rendered thumbnail. */
  thumb?: { url: string; alt: string } | null;
  /**
   * What one whole turn is worth: the dial's gear. A day by default, as the rim's clock face
   * has always implied; pinching asks the page for a different one.
   */
  turnMs?: number;
  onScrub: (t: number) => void;
  /** True while a finger is on the dial, false the moment it lifts. */
  onHold?: (holding: boolean) => void;
  /**
   * Two fingers spread or brought together: the gear the dial should be in now. The page owns
   * the gear, because the detents and the rim are built from it.
   */
  onZoom?: (turnMs: number) => void;
}) {
  const labelId = useId();
  const still = useMediaQuery("(prefers-reduced-motion: reduce)");
  const bounds = useMemo(
    () => ({ min: 0, max: angleForTime(to, from, turnMs), boundaries }),
    [from, to, boundaries, turnMs],
  );

  // The reducer's state lives in a ref: it is stepped from an animation frame, and re-rendering
  // per frame would be the one thing that makes a 60 Hz dial stutter.
  const dial = useRef<DialState>({
    angle: angleForTime(value, from, turnMs),
    velocity: 0,
    mode: "rest",
  });
  const [angle, setAngle] = useState(dial.current.angle);
  const frame = useRef<number | null>(null);
  const last = useRef(0);
  const drag = useRef<{ id: number; x: number; at: number; start: number } | null>(null);
  /** The live pinch: the span the gear was last changed at, and the gear it was changed to. */
  const pinch = useRef<{ a: number; b: number; span: number; gear: number } | null>(null);
  const pinched = useRef<Map<number, number>>(new Map());
  const scrub = useRef(onScrub);
  scrub.current = onScrub;
  const hold = useRef(onHold);
  hold.current = onHold;

  // A new gear: the same moment is now at a different angle, so the needle is put back on it.
  // Unconditional, unlike the follow below -- the gear only ever changes mid-gesture, during a
  // pinch, and that is exactly when the angle must be re-read.
  useEffect(() => {
    const want = angleForTime(value, from, turnMs);
    dial.current = { angle: want, velocity: 0, mode: "rest" };
    setAngle(want);
    // Deliberately only the gear: `value` and `from` have their own effect below, and listing
    // them here would re-anchor on every scrub.
  }, [turnMs]);

  // The playhead moved from somewhere else (a filter changed, the page landed): follow it, but
  // never while the traveller's own finger owns the dial.
  useEffect(() => {
    if (dial.current.mode !== "rest") return;
    const want = angleForTime(value, from, turnMs);
    if (Math.abs(want - dial.current.angle) < 1e-9) return;
    dial.current = { angle: want, velocity: 0, mode: "rest" };
    setAngle(want);
  }, [value, from]);

  const stop = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);

  const run = useCallback(() => {
    if (frame.current !== null) return;
    last.current = performance.now();
    const tick = (now: number) => {
      frame.current = null;
      const dt = now - last.current;
      last.current = now;
      const r = step(dial.current, dt, detents, { ...bounds, magnetic });
      dial.current = r.state;
      setAngle(r.state.angle);
      scrub.current(timeForAngle(r.state.angle, from, turnMs));
      for (let i = 0; i < r.crossed.length; i++) vibrate(HAPTIC_MS);
      // At rest the loop stops. It does not idle on a phone that never sleeps.
      if (r.state.mode !== "rest") frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
  }, [bounds, detents, from]);

  useEffect(() => stop, [stop]);

  /**
   * Gearing: two fingers spread or brought together. Each time the span between them grows or
   * shrinks by `PINCH_STEP`, the dial moves one gear and the span is measured afresh, so a long
   * spread walks through the gears rather than jumping to the end of them.
   *
   * The needle does not move: the page keeps the playhead and hands back a new gear, and the
   * effect below re-anchors the angle to the same moment. Pinching is a change of scale, not a
   * scrub, and a pinch that also scrubbed would lose the moment being looked at.
   */
  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (drag.current && onZoom) {
      // A second finger: the gesture is a pinch, so the drag it interrupts is abandoned rather
      // than finished — no flick, no settle, and nothing scrubbed by the hand changing shape.
      pinch.current = {
        a: drag.current.x,
        b: e.clientX,
        span: Math.abs(e.clientX - drag.current.x),
        gear: turnMs,
      };
      pinched.current = new Map([
        [drag.current.id, drag.current.x],
        [e.pointerId, e.clientX],
      ]);
      drag.current = null;
      hold.current?.(false);
      return;
    }
    if (drag.current) return; // one finger owns the dial; a second is ignored, not merged
    stop();
    // Said before the capture is asked for: whether the browser grants it or not, a finger is
    // on the dial, and whatever is waiting for the dial to be left alone needs to know now.
    hold.current?.(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      id: e.pointerId,
      x: e.clientX,
      at: performance.now(),
      start: dial.current.angle,
    };
    dial.current = { ...dial.current, mode: "drag", velocity: 0 };
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const p = pinch.current;
    if (p) {
      if (!pinched.current.has(e.pointerId)) return;
      pinched.current.set(e.pointerId, e.clientX);
      const xs = [...pinched.current.values()];
      const span = Math.abs((xs[0] ?? 0) - (xs[1] ?? 0));
      if (span < 1 || p.span < 1) return;
      const ratio = span / p.span;
      if (ratio >= PINCH_STEP || ratio <= 1 / PINCH_STEP) {
        const next = nextGear(p.gear, ratio >= PINCH_STEP ? "in" : "out");
        p.gear = next;
        p.span = span;
        onZoom?.(next);
      }
      return;
    }
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const now = performance.now();
    const dx = e.clientX - d.x;
    // A floor on the sampling gap: pointer events can arrive coalesced, several in the same
    // millisecond, and dividing by that reads as a flick nobody made.
    const dt = Math.max(MIN_SAMPLE_MS, now - d.at);
    // Dragging left turns the dial forward, the way a physical wheel under a thumb would.
    // The boundary takes its share of the finger's travel before anything else sees it, so the
    // dial resists under the thumb and not only once it has been let go.
    const delta = geared(dial.current.angle, -dx / pxPerRadian(window.innerWidth), bounds);
    const raw = (delta / dt) * 1000;
    const smoothed = dial.current.velocity * SMOOTHING + raw * (1 - SMOOTHING);
    const next = dragTo(dial.current, dial.current.angle + delta, smoothed, bounds);
    const crossedNow = crossings(detents, dial.current.angle, next.angle);
    dial.current = next;
    d.x = e.clientX;
    d.at = now;
    setAngle(next.angle);
    scrub.current(timeForAngle(next.angle, from, turnMs));
    for (let i = 0; i < crossedNow; i++) vibrate(HAPTIC_MS);
  }

  function endDrag(e: React.PointerEvent<HTMLDivElement>) {
    if (pinch.current) {
      pinched.current.delete(e.pointerId);
      // The pinch is over when a finger leaves. The one still down does not take the dial back:
      // it would turn it by the width of the hand opening.
      if (pinched.current.size < 2) {
        pinch.current = null;
        pinched.current.clear();
      }
      return;
    }
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    hold.current?.(false);
    if (still) {
      // No flick and no settle: let go and you are on the nearest moment of the day you are in.
      // A day that holds nothing holds you where you left off rather than pulling you out of it,
      // and with the magnet off nothing pulls at all.
      dial.current = magnetic
        ? snap(dial.current, settleOn ?? detents)
        : { ...dial.current, velocity: 0, mode: "rest" };
      setAngle(dial.current.angle);
      scrub.current(timeForAngle(dial.current.angle, from, turnMs));
      return;
    }
    dial.current = release(dial.current, dial.current.velocity, bounds);
    run();
  }

  function moveTo(t: number) {
    stop();
    const clamped = Math.min(to, Math.max(from, t));
    const a = angleForTime(clamped, from, turnMs);
    dial.current = { angle: a, velocity: 0, mode: "rest" };
    setAngle(a);
    scrub.current(clamped);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const here = timeForAngle(dial.current.angle, from, turnMs);
    const stepBy = (ms: number) => {
      e.preventDefault();
      moveTo(here + ms);
    };
    if (e.key === "ArrowRight" || e.key === "ArrowUp") return toDetent(1, e);
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") return toDetent(-1, e);
    if (e.key === "PageUp") return stepBy(3_600_000);
    if (e.key === "PageDown") return stepBy(-3_600_000);
    if (e.key === "Home") return stepBy(from - here);
    if (e.key === "End") return stepBy(to - here);
  }

  /** Arrow keys move recording to recording — the detents are the interesting moments. */
  function toDetent(direction: 1 | -1, e: React.KeyboardEvent<HTMLDivElement>) {
    e.preventDefault();
    const a = dial.current.angle;
    const next =
      direction === 1
        ? detents.find((d) => d.angle > a + 1e-9)
        : [...detents].reverse().find((d) => d.angle < a - 1e-9);
    if (next) moveTo(next.t);
    else moveTo(direction === 1 ? to : from);
  }

  const marks = useMemo(
    () => marksFor(angle - ARC / 2, angle + ARC / 2, from, timeZone, undefined, turnMs),
    [angle, from, timeZone],
  );
  const visibleDetents = useMemo(
    () => detents.filter((d) => Math.abs(d.angle - angle) <= ARC / 2),
    [detents, angle],
  );

  return (
    <div className="dial" data-testid="time-dial">
      <p className="dial-readout" id={labelId} aria-live="polite">
        {thumb && <img className="dial-thumb" src={thumb.url} alt={thumb.alt} />}
        {readout}
      </p>
      <div
        className="dial-stage"
        role="slider"
        tabIndex={0}
        aria-labelledby={labelId}
        aria-valuemin={from}
        aria-valuemax={to}
        aria-valuenow={value}
        aria-valuetext={readout}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
      >
        <div className="dial-disk">
          <div className="dial-rim" style={{ transform: `rotate(${-angle}rad)` }}>
            {marks.map((m) => (
              <div
                key={`${m.kind}-${m.t}`}
                className={`dial-mark ${m.kind}`}
                style={{ transform: `rotate(${m.angle}rad)` }}
              >
                <span className="dial-tick" />
                {/* The label is counter-rotated by the rim's turn and its own spoke, so it
                    stays upright however far the dial has been turned. */}
                {m.label !== undefined && Math.abs(m.angle - angle) <= LABEL_ARC / 2 && (
                  <span
                    className="dial-label"
                    style={{ transform: `rotate(${angle - m.angle}rad)` }}
                  >
                    {m.label}
                  </span>
                )}
              </div>
            ))}
            {visibleDetents.map((d) => (
              <div
                key={`detent-${d.t}`}
                className="dial-mark detent"
                style={{ transform: `rotate(${d.angle}rad)` }}
              >
                <span className="dial-dot" />
              </div>
            ))}
          </div>
        </div>
        <span className="dial-needle" aria-hidden="true" />
      </div>
    </div>
  );
}

/** How many detents sit between two angles. Counting only; the reducer owns the physics. */
function crossings(detents: Detent[], from: number, to: number): number {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  return detents.filter((d) => d.angle > lo && d.angle <= hi).length;
}

/** Re-exported so the page can place the playhead on a detent without importing the reducer. */
export { nearestDetent };
